// Per-question charts (5 chart+table pairs, or "No data" when contributors === 0).
//
// Charts: Preset/Mixed Responses, Theme Engagement, Contributors by Theme,
// Responses by Theme, Unique Responses and Matches by Theme.
// Reuses ChartTablePair from ../survey/chart-table-pair.

import React from "react";
import { Typography } from "@material-tailwind/react";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";
import {
    calculateLeftMargin,
    truncateLabel,
    tooltipLabelMap,
    CHART_BASE_MARGIN,
} from "../data-transforms";
import {
    ChartTablePair,
    COMMON_CHART_PROPS,
    buildEngagementLegend,
    ResponsiveBar,
    BasicTooltip,
} from "../survey/chart-table-pair";

// Tooltip for the Theme Engagement chart: percentage is value / denominator
// where denominator depends on which series id is being hovered.
function makeThemeEngagementTooltip(qData) {
    return ({ indexValue, value, color, id }) => {
        let denominator;
        if (id === "contributors") {
            denominator = qData.contributors || 0;
        } else if (id === "responses") {
            denominator = qData.responses || 0;
        } else if (id === "uniqueResponses") {
            denominator = qData.uniqueResponses || 0;
        }
        const percentage = denominator > 0 ? ((value / denominator) * 100).toFixed(1) : 0;
        return (
            <BasicTooltip
                id={`${truncateLabel(indexValue, 10)} - ${tooltipLabelMap[id] || id}: ${value} (${percentage}%)`}
                enableChip={true}
                color={color}
            />
        );
    };
}

// Tooltip for the stacked Unique+Matches chart: percentage is value /
// theme.responses for the hovered theme. The theme id is read directly
// from the Nivo datum (each chart datum has { label, id, uniqueResponses,
// votes } - see the data prop on the ResponsiveBar at the call site).
// The previous implementation parsed the label by stripping a rank
// prefix that does not exist in the data, so the substring never
// matched and the percentage always showed 0%.
function makeUniqueMatchesTooltip(qData) {
    return ({ datum, indexValue, value, color, id }) => {
        const themeData = qData.themesEngagement.find(
            (t) => t.id === datum?.id,
        );
        const themeResponsesTotal = themeData?.responses || 0;
        const percentage = themeResponsesTotal > 0
            ? ((value / themeResponsesTotal) * 100).toFixed(1)
            : 0;
        return (
            <BasicTooltip
                id={`${truncateLabel(indexValue, 10)} - ${tooltipLabelMap[id] || id}: ${value} (${percentage}%)`}
                enableChip={true}
                color={color}
            />
        );
    };
}

export function QuestionCharts({
    qData,
    openChartTables,
    toggleChartTable,
    legendDirection,
    getLegendPosition,
    getBottomMargin,
    getBottomMarginNoLegend,
    getLegendItemWidth,
}) {
    const legendHelpers = { legendDirection, getLegendPosition, getLegendItemWidth };

    return (
        <div className="mb-4">
            <Typography variant="h5" className="mb-2">Charts</Typography>
            {qData.contributors === 0 ? (
                <Typography className="text-gray-500">No data available for this question.</Typography>
            ) : (
                <>
                    {/* 1. Preset/Mixed Responses — full width, single-key horizontal bar */}
                    {((qData.type === "Preset" && qData.presetData?.length > 0) ||
                            (qData.type === "Mixed" && qData.presetData?.length > 0)) && (
                        <ChartTablePair
                            title={`Question ${qData.number} ${qData.type === "Mixed" ? "Mixed Responses" : "Preset Responses"}`}
                            visible
                            yAxisLabel="Response options"
                            chartHeight="400px"
                            toggleKey={`q${qData.number}-preset`}
                            openChartTables={openChartTables}
                            toggleChartTable={toggleChartTable}
                            className="mb-6"
                            chart={
                                <ResponsiveBar
                                    {...COMMON_CHART_PROPS}
                                    data={qData.presetDataForChart.map((q) => ({
                                        label: `${truncateLabel(typeof q.id === "string" ? q.id : q.id?.text, 30)}`,
                                        responses: q.responses,
                                    }))}
                                    keys={["responses"]}
                                    margin={{
                                        ...CHART_BASE_MARGIN,
                                        bottom: getBottomMargin(1),
                                        left: calculateLeftMargin(
                                            qData.presetDataForChart.map((q) =>
                                                `${truncateLabel(typeof q.id === "string" ? q.id : q.id?.text, 30)}`,
                                            ).map((l) => truncateLabel(l, 10)),
                                            { charWidth: 7, padding: 15 },
                                        ),
                                    }}
                                    colors={["#fdc403"]}
                                    axisBottom={{
                                        tickSize: 5,
                                        tickPadding: 5,
                                        tickRotation: 0,
                                        legend: "Responses",
                                        legendPosition: "middle",
                                        legendOffset: 40,
                                    }}
                                    labelPosition="end"
                                    labelOffset={4}
                                    tooltip={({ indexValue, value, color }) => (
                                        <BasicTooltip
                                            id={`${truncateLabel(indexValue, 20)}: ${value}`}
                                            enableChip={true}
                                            color={color}
                                        />
                                    )}
                                    aria-label={`${qData.text} Response Distribution`}
                                />
                            }
                            table={
                                <GobbyTable>
                                    <GobbyTableHead>
                                        <GobbyTableRow>
                                            <GobbyTableHeader>Response</GobbyTableHeader>
                                            <GobbyTableHeader>Count</GobbyTableHeader>
                                        </GobbyTableRow>
                                    </GobbyTableHead>
                                    <GobbyTableBody>
                                        {qData.presetData.map((q, index) => (
                                            <GobbyTableRow key={index}>
                                                <GobbyTableCell>
                                                    {truncateLabel(q.id?.text || q.id, 40)}
                                                </GobbyTableCell>
                                                <GobbyTableCell>{q.responses}</GobbyTableCell>
                                            </GobbyTableRow>
                                        ))}
                                    </GobbyTableBody>
                                </GobbyTable>
                            }
                        />
                    )}

                    {/* 2-5. Gobby-only theme charts */}
                    {(qData.type === "Gobby" && qData.themesEngagement?.length > 0) && (
                        <>
                            {/* 2. Main Theme Engagement — full width, 3-key grouped */}
                            <ChartTablePair
                                title={`Question ${qData.number} Theme Engagement`}
                                visible
                                yAxisLabel="Themes"
                                chartHeight="400px"
                                toggleKey={`q${qData.number}-themeEngagement`}
                                openChartTables={openChartTables}
                                toggleChartTable={toggleChartTable}
                                className="mb-4"
                                chart={
                                    <ResponsiveBar
                                        {...COMMON_CHART_PROPS}
                                        data={qData.themesByTotalEngagementForChart.slice(0, 10).map((t) => ({
                                            label: `${truncateLabel(t.id, 30)}`,
                                            id: t.id,
                                            contributors: t.contributors,
                                            responses: t.responses,
                                            uniqueResponses: t.uniqueResponses,
                                        }))}
                                        groupMode="grouped"
                                        keys={["contributors", "responses", "uniqueResponses"]}
                                        margin={{
                                            ...CHART_BASE_MARGIN,
                                            bottom: getBottomMargin(3),
                                            left: calculateLeftMargin(
                                                qData.themesByTotalEngagementForChart.slice(0, 10).map((t) =>
                                                    `${truncateLabel(t.id, 30)}`,
                                                ).map((l) => truncateLabel(l, 10)),
                                                { charWidth: 7, padding: 15 },
                                            ),
                                        }}
                                        colors={["#fdc403", "#aaa9a9", "#c5c5c5"]}
                                        axisBottom={{
                                            tickSize: 5,
                                            tickPadding: 5,
                                            tickRotation: 0,
                                            legend: "Count",
                                            legendPosition: "middle",
                                            legendOffset: 40,
                                        }}
                                        labelPosition="end"
                                        labelOffset={4}
                                        legends={buildEngagementLegend(
                                            ["contributors", "responses", "uniqueResponses"],
                                            ["#fdc403", "#aaa9a9", "#c5c5c5"],
                                            legendHelpers,
                                        )}
                                        tooltip={makeThemeEngagementTooltip(qData)}
                                        aria-label={`Question ${qData.number} Theme Engagement`}
                                    />
                                }
                                table={
                                    <GobbyTable>
                                        <GobbyTableHead>
                                            <GobbyTableRow>
                                                <GobbyTableHeader>Theme</GobbyTableHeader>
                                                <GobbyTableHeader>Contributors</GobbyTableHeader>
                                                <GobbyTableHeader>Responses</GobbyTableHeader>
                                                <GobbyTableHeader>Unique Responses</GobbyTableHeader>
                                            </GobbyTableRow>
                                        </GobbyTableHead>
                                        <GobbyTableBody>
                                            {qData.themesByTotalEngagement.map((t, index) => (
                                                <GobbyTableRow key={index}>
                                                    <GobbyTableCell>{truncateLabel(t.id, 40)}</GobbyTableCell>
                                                    <GobbyTableCell>{t.contributors}</GobbyTableCell>
                                                    <GobbyTableCell>{t.responses}</GobbyTableCell>
                                                    <GobbyTableCell>{t.uniqueResponses}</GobbyTableCell>
                                                </GobbyTableRow>
                                            ))}
                                        </GobbyTableBody>
                                    </GobbyTable>
                                }
                            />

                            {/* 3-5. Three single-metric theme charts in a 2-col grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4 mb-4">
                                {/* 3. Contributors by Theme */}
                                <ChartTablePair
                                    title={`Question ${qData.number} Contributors by Theme`}
                                    visible
                                    yAxisLabel="Themes"
                                    chartHeight="300px"
                                    toggleKey={`q${qData.number}-themeContributors`}
                                    openChartTables={openChartTables}
                                    toggleChartTable={toggleChartTable}
                                    chart={
                                        <ResponsiveBar
                                            {...COMMON_CHART_PROPS}
                                            data={qData.themesByContributorsForChart.slice(0, 10).map((t) => ({
                                                label: `${truncateLabel(t.id, 30)}`,
                                                contributors: t.contributors,
                                            }))}
                                            keys={["contributors"]}
                                            margin={{
                                                ...CHART_BASE_MARGIN,
                                                bottom: getBottomMarginNoLegend(),
                                                left: calculateLeftMargin(
                                                    qData.themesByContributorsForChart.slice(0, 10).map((t) =>
                                                        `${truncateLabel(t.id, 30)}`,
                                                    ).map((l) => truncateLabel(l, 10)),
                                                    { charWidth: 7, padding: 15 },
                                                ),
                                            }}
                                            colors={["#fdc403"]}
                                            axisBottom={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: "Contributors",
                                                legendPosition: "middle",
                                                legendOffset: 40,
                                            }}
                                            labelPosition="end"
                                            labelOffset={4}
                                        />
                                    }
                                    table={
                                        <GobbyTable>
                                            <GobbyTableHead>
                                                <GobbyTableRow>
                                                    <GobbyTableHeader>Theme</GobbyTableHeader>
                                                    <GobbyTableHeader>Contributors</GobbyTableHeader>
                                                </GobbyTableRow>
                                            </GobbyTableHead>
                                            <GobbyTableBody>
                                                {qData.themesByContributors.map((t, index) => (
                                                    <GobbyTableRow key={index}>
                                                        <GobbyTableCell>{truncateLabel(t.id, 40)}</GobbyTableCell>
                                                        <GobbyTableCell>{t.contributors}</GobbyTableCell>
                                                    </GobbyTableRow>
                                                ))}
                                            </GobbyTableBody>
                                        </GobbyTable>
                                    }
                                />

                                {/* 4. Responses by Theme */}
                                <ChartTablePair
                                    title={`Question ${qData.number} Responses by Theme`}
                                    visible
                                    yAxisLabel="Themes"
                                    chartHeight="300px"
                                    toggleKey={`q${qData.number}-themeResponses`}
                                    openChartTables={openChartTables}
                                    toggleChartTable={toggleChartTable}
                                    chart={
                                        <ResponsiveBar
                                            {...COMMON_CHART_PROPS}
                                            data={qData.themesByResponsesForChart.slice(0, 10).map((t) => ({
                                                label: `${truncateLabel(t.id, 30)}`,
                                                responses: t.responses,
                                            }))}
                                            keys={["responses"]}
                                            margin={{
                                                ...CHART_BASE_MARGIN,
                                                bottom: getBottomMarginNoLegend(),
                                                left: calculateLeftMargin(
                                                    qData.themesByResponsesForChart.slice(0, 10).map((t) =>
                                                        `${truncateLabel(t.id, 30)}`,
                                                    ).map((l) => truncateLabel(l, 10)),
                                                    { charWidth: 7, padding: 15 },
                                                ),
                                            }}
                                            colors={["#aaa9a9"]}
                                            axisBottom={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: "Responses",
                                                legendPosition: "middle",
                                                legendOffset: 40,
                                            }}
                                            labelPosition="end"
                                            labelOffset={4}
                                        />
                                    }
                                    table={
                                        <GobbyTable>
                                            <GobbyTableHead>
                                                <GobbyTableRow>
                                                    <GobbyTableHeader>Theme</GobbyTableHeader>
                                                    <GobbyTableHeader>Responses</GobbyTableHeader>
                                                </GobbyTableRow>
                                            </GobbyTableHead>
                                            <GobbyTableBody>
                                                {qData.themesByResponses.map((t, index) => (
                                                    <GobbyTableRow key={index}>
                                                        <GobbyTableCell>{truncateLabel(t.id, 40)}</GobbyTableCell>
                                                        <GobbyTableCell>{t.responses}</GobbyTableCell>
                                                    </GobbyTableRow>
                                                ))}
                                            </GobbyTableBody>
                                        </GobbyTable>
                                    }
                                />

                                {/* 5. Unique Responses and Matches by Theme — stacked */}
                                <ChartTablePair
                                    title={`Question ${qData.number} Unique Responses and Matches by Theme`}
                                    visible
                                    yAxisLabel="Themes"
                                    chartHeight="300px"
                                    toggleKey={`q${qData.number}-themeUniqueMatches`}
                                    openChartTables={openChartTables}
                                    toggleChartTable={toggleChartTable}
                                    chart={
                                        <ResponsiveBar
                                            {...COMMON_CHART_PROPS}
                                            data={qData.themesByUniqueAndVotesForChart.slice(0, 10).map((t) => ({
                                                label: `${truncateLabel(t.id, 30)}`,
                                                id: t.id,
                                                uniqueResponses: t.uniqueResponses,
                                                votes: t.votes,
                                            }))}
                                            groupMode="stacked"
                                            keys={["uniqueResponses", "votes"]}
                                            margin={{
                                                ...CHART_BASE_MARGIN,
                                                bottom: getBottomMargin(2),
                                                left: calculateLeftMargin(
                                                    qData.themesByUniqueAndVotesForChart.slice(0, 10).map((t) =>
                                                        `${truncateLabel(t.id, 30)}`,
                                                    ).map((l) => truncateLabel(l, 10)),
                                                    { charWidth: 7, padding: 15 },
                                                ),
                                            }}
                                            colors={["#c5c5c5", "#aaa9a9"]}
                                            axisBottom={{
                                                tickSize: 5,
                                                tickPadding: 5,
                                                tickRotation: 0,
                                                legend: "Count",
                                                legendPosition: "middle",
                                                legendOffset: 40,
                                            }}
                                            labelPosition="middle"
                                            legends={buildEngagementLegend(
                                                ["uniqueResponses", "votes"],
                                                ["#c5c5c5", "#aaa9a9"],
                                                legendHelpers,
                                            )}
                                            tooltip={makeUniqueMatchesTooltip(qData)}
                                            aria-label={`Question ${qData.number} Unique Responses and Matches by Theme`}
                                        />
                                    }
                                    table={
                                        <GobbyTable>
                                            <GobbyTableHead>
                                                <GobbyTableRow>
                                                    <GobbyTableHeader>Theme</GobbyTableHeader>
                                                    <GobbyTableHeader>Unique Responses</GobbyTableHeader>
                                                    <GobbyTableHeader>Matched Responses</GobbyTableHeader>
                                                </GobbyTableRow>
                                            </GobbyTableHead>
                                            <GobbyTableBody>
                                                {qData.themesByUniqueAndVotes.map((t, index) => (
                                                    <GobbyTableRow key={index}>
                                                        <GobbyTableCell>{truncateLabel(t.id, 40)}</GobbyTableCell>
                                                        <GobbyTableCell>{t.uniqueResponses}</GobbyTableCell>
                                                        <GobbyTableCell>{t.votes}</GobbyTableCell>
                                                    </GobbyTableRow>
                                                ))}
                                            </GobbyTableBody>
                                        </GobbyTable>
                                    }
                                />
                            </div>
                        </>
                    )}

                </>
            )}
        </div>
    );
}
